// src/Pages/ReAssignInquiryDetails.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Grid, Paper, Typography, Button, Box, Checkbox, CircularProgress, Chip, Divider, Snackbar, Avatar, useMediaQuery, useTheme } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkIcon from "@mui/icons-material/Link";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Dropdown from "../components/Dropdown";

const ReAssignInquiryDetails = ({ loggedInUser }) => {
    const [statusOptions, setStatusOptions] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [selectedInquiries, setSelectedInquiries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [applyClicked, setApplyClicked] = useState(false);

    const [filters, setFilters] = useState({
        assign: "", assignName: "",
        reAssign: "", reAssignName: "",
        status: "", statusValue: ""
    });

    const path = "http://192.168.1.248:3103";
    const avatarColors = ["#6f42c1", "#007bff", "#20c997", "#fd7e14", "#e83e8c", "#6c757d"];

    useEffect(() => {
        axios.get(`${path}/api/status`).then((res) => setStatusOptions(res.data.data || []));
        axios.get(`${path}/api/users`, { params: { companyId: loggedInUser.companyId } }).then((res) => setUsersOptions(Array.isArray(res.data.data) ? res.data.data : []));
    }, [path]);

    const handleAssignChange = (selected) => setFilters(prev => ({ ...prev, assign: selected.userId, assignName: selected.employeeName }));
    const handleReAssignChange = (selected) => setFilters(prev => ({ ...prev, reAssign: selected.userId, reAssignName: selected.employeeName }));
    const handleStatusChange = (selected) => setFilters(prev => ({ ...prev, status: selected.LeadStatusID, statusValue: selected.LeadStatus }));

    const FN_FetchInquiries = () => {
        setApplyClicked(true);
        setLoading(true);
        axios.get(`${path}/api/reassign-inquiry`, { params: { assign: filters.assignName, status: filters.statusValue } })
            .then((res) => {
                setInquiries(res.data.data || []);
                setSelectedInquiries([]);
            })
            .finally(() => setLoading(false));
    };

    const HDL_HandleResetFilters = () => {
        setFilters({ assign: "", assignName: "", reAssign: "", reAssignName: "", status: "", statusValue: "" });
        setInquiries([]); setSelectedInquiries([]);
    };

    const HDL_HandleCheckbocChange = (inquiryId) => {
        setSelectedInquiries((prev) => prev.includes(inquiryId) ? prev.filter((id) => id !== inquiryId) : [...prev, inquiryId]);
    };

    const HDL_HandleSelectAll = (event) => {
        setSelectedInquiries(event.target.checked ? inquiries.map((inq) => inq.inquiryId) : []);
    };

    const HDL_HandleSubmit = () => {
        if (!filters.assign || !filters.reAssign || selectedInquiries.length === 0 || (filters.assign === filters.reAssign && filters.assignName === filters.reAssignName)) {
            alert("Invalid input or selection."); return;
        }

        const payload = {
            assignId: filters.assign,
            assign: filters.assignName,
            reAssignId: filters.reAssign,
            reAssign: filters.reAssignName,
            status: filters.statusValue,
            inquiryIds: selectedInquiries,
        };

        axios.post(`${path}/api/reassign-inquiries`, payload)
            .then(() => {
                alert("Submission successful!");
                HDL_HandleResetFilters();
            })
            .catch(() => alert("Submission failed."));
    };

    const HLP_FormatYMDToDMY = (date) => {
        if (!date) return "—";
        const d = new Date(date);
        return d.toLocaleDateString("en-GB");
    };

    const HLP_GetPriorityColor = (priority) => {
        switch (priority?.toLowerCase()) {
            case "high": return "#e53935";
            case "medium": return "#fb8c00";
            case "low": return "#43a047";
            default: return "#9e9e9e";
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Section Title */}
            <Typography variant="h5" fontWeight={600} gutterBottom sx={{ color: 'text.primary' }}>
                Re-Assign Inquiries
            </Typography>

            {/* Filter Card */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, bgcolor: '#f9fafb', boxShadow: '0px 2px 8px rgba(0,0,0,0.04)', mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2, color: 'text.secondary' }}> Search Filters </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Dropdown label="Assigned User" value={filters.assign} options={usersOptions} valueKey="userId" labelKey="employeeName" onChange={handleAssignChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Dropdown label="Re-Assign To" value={filters.reAssign} options={usersOptions} valueKey="userId" labelKey="employeeName" onChange={handleReAssignChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Dropdown label="Status" value={filters.status} options={statusOptions} valueKey="LeadStatusID" labelKey="LeadStatus" onChange={handleStatusChange} fullWidth />
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="contained" color="success" onClick={FN_FetchInquiries} sx={{ minWidth: 120, fontWeight: 500 }} > Apply </Button>
                    <Button variant="contained" color="primary" onClick={HDL_HandleSubmit} sx={{ minWidth: 120, fontWeight: 500 }} > Submit </Button>
                    <Button variant="outlined" color="inherit" onClick={HDL_HandleResetFilters} sx={{ minWidth: 120, fontWeight: 500 }} > Reset </Button>
                </Box>
            </Paper>

            {/* Select All Checkbox */}
            {!loading && inquiries.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Checkbox onChange={HDL_HandleSelectAll} checked={inquiries.every(inq => selectedInquiries.includes(inq.inquiryId))} />
                    <Typography variant="body1" sx={{ ml: 1 }}> Select All ({selectedInquiries.length}/{inquiries.length}) </Typography>
                </Box>
            )}

            {loading ? (
                <Box sx={{ textAlign: "center", mt: 4 }}><CircularProgress /></Box>
            ) : (
                applyClicked && filters.assign && inquiries.length === 0 ? (
                    <Typography align="center" sx={{ mt: 4 }} color="text.secondary"> No inquiry found of selected user </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {inquiries.map((inq, idx) => {
                            const color = avatarColors[idx % avatarColors.length];
                            const priorityColor = HLP_GetPriorityColor(inq.priority);
                            const pLetter = inq.priority?.charAt(0).toUpperCase() || "—";
                            return (
                                <Grid item xs={12} key={inq.inquiryId}>
                                    <Paper elevation={2} sx={{
                                        borderLeft: `6px solid ${priorityColor}`,
                                        borderRadius: 1,
                                        p: { xs: 2, md: 3 },
                                        bgcolor: "background.paper",
                                        position: "relative",
                                        cursor: "pointer",
                                        "&:hover": { boxShadow: 4 }
                                    }}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                                            <Checkbox size="small" sx={{ mr: 1 }} value={inq.inquiryId} onClick={e => e.stopPropagation()} onChange={e => HDL_HandleCheckbocChange(inq.inquiryId)} checked={selectedInquiries.includes(inq.inquiryId)} />
                                            <Avatar sx={{ bgcolor: color, width: 45, height: 45, mr: 1.5 }}> {inq.clientname?.[0]?.toUpperCase() || "?"} </Avatar>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="h6" noWrap fontWeight={500} sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }} > {inq.clientname} </Typography>
                                                <Typography variant="caption" color="text.secondary">{inq.city}</Typography>
                                            </Box>
                                            <Chip label={`${inq.statusName} | ${pLetter}`} size="small" sx={{ bgcolor: priorityColor, color: "#fff", ml: 1, opacity: 0.7 }} />
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                <PersonIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                                                <Typography variant="body2" noWrap>{inq.clientContact || "—"}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                                <LinkIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                                                <Typography variant="body2" noWrap>{inq.LastleadSourceName || "—"}</Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <PhoneIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                                                <Typography variant="body2" color="primary" noWrap>{inq.mobileNo || "—"}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Typography fontSize={16} fontWeight={600} color="grey.700" sx={{ mr: 0.5 }}>₹</Typography>
                                                <Typography variant="body2" fontWeight="600" noWrap>{inq.closingPrice ?? "0.00"}</Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ mb: 1 }} />

                                        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <AssignmentIndIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                                                <Typography variant="body2" noWrap>{inq.employeeName || "—"}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: "grey.600" }} />
                                                <Typography variant="body2" noWrap>{HLP_FormatYMDToDMY(inq.nextFollowUpDate)}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )
            )}

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} message={snackbarMessage} />
        </Container>
    );
};

export default ReAssignInquiryDetails;